import clsx from "clsx";
import React from "react";
import { getTypographyClassName } from "@appsmith/wds-theming";
import { Button, ColorSwatch } from "react-aria-components";

// Relative imports (not the "@appsmith/wds" barrel) keep this component out
// of the package-barrel dependency cycle counted by the CI cyclic-deps gate.
import type { SIZES } from "../../../shared";
import { textInputStyles } from "../../Input";
import { Spinner } from "../../Spinner";

import styles from "./styles.module.css";

interface ColorPickerTriggerProps {
  /** Accessible name of the trigger — RAC's colorName for the CURRENT color
   * (architect decision 5), or "transparent" when unset. */
  colorName: string;
  /** Committed hex text shown beside the swatch ("" when unset). */
  hexText: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  size?: Exclude<keyof typeof SIZES, "xSmall" | "large">;
}

/**
 * Swatch trigger: a real button (fed by DialogTrigger's ButtonContext) showing
 * the current color swatch over a checkerboard underlay — so alpha and the
 * unset state read correctly — plus the committed hex text. The suffix slot
 * carries the spinner while isLoading (M3 async contract), during which the
 * trigger is non-interactive.
 */
export const ColorPickerTrigger = (props: ColorPickerTriggerProps) => {
  const { colorName, hexText, isDisabled, isLoading, size } = props;

  return (
    <div className={textInputStyles.inputGroup}>
      <Button
        aria-label={colorName}
        className={clsx(
          textInputStyles.input,
          styles.trigger,
          getTypographyClassName("body"),
        )}
        data-color-picker-trigger
        data-size={size}
        isDisabled={Boolean(isDisabled) || Boolean(isLoading)}
      >
        {/* The swatch is decorative here — the button itself is named after
            the color; hiding it avoids a double announcement. */}
        <span aria-hidden="true" className={styles.swatchUnderlay}>
          <ColorSwatch className={styles.swatch} />
        </span>
        <span data-color-picker-hex>{hexText}</span>
      </Button>
      {Boolean(isLoading) && (
        <span data-input-suffix>
          <Spinner />
        </span>
      )}
    </div>
  );
};
